import React from 'react';
import { Alert, Input } from 'antd';
// 결제 설정과 같은 '결제 관리' 메뉴라 확인 모달의 모양·간격을 공유한다.
import { SettingsModal } from '../../pgProviderRoute/common/SettingsModal';
import { SettingChangesStyle } from '../../pgProviderRoute/common/styles';
import { FeeChange, ReverseMarginFee } from '../types';

export const REASON_MIN_LENGTH = 2;
export const REASON_MAX_LENGTH = 500;

type FeeReviewModalProps = {
  open: boolean;
  changes: FeeChange[];
  reverseMarginFees: ReverseMarginFee[];
  reason: string;
  isSaving: boolean;
  error: string | null;
  onReasonChange: (reason: string) => void;
  onSave: () => void;
  onClose: () => void;
};

export const FeeReviewModal = (props: FeeReviewModalProps): React.JSX.Element => {
  const { open, changes, reverseMarginFees, reason, isSaving, error, onReasonChange, onSave, onClose } = props;
  const isReasonValid = reason.trim().length >= REASON_MIN_LENGTH;

  return (
    <SettingsModal
      destroyOnClose
      open={open}
      title="수수료 변경 검토"
      width={700}
      okText="변경 사항 저장"
      cancelText="계속 편집"
      onOk={onSave}
      onCancel={onClose}
      confirmLoading={isSaving}
      okButtonProps={{ disabled: !isReasonValid }}
      cancelButtonProps={{ disabled: isSaving }}
      closable={!isSaving}
      maskClosable={!isSaving}
      keyboard={!isSaving}
    >
      <p>
        정산용 값을 바꾸면 <b>다음 정산 생성분부터</b> 반영됩니다. 이미 만들어진 정산은 금액이 확정 저장돼 있어 바뀌지
        않습니다.
      </p>
      {error && <Alert type="error" showIcon message={error} />}
      {reverseMarginFees.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="명목 요율이 실제 요율보다 낮은 행이 있습니다."
          description={
            <>
              {reverseMarginFees.map((fee) => (
                <div key={fee.id}>
                  {fee.pgProviderKorName} · {fee.payMethodName} — 명목 {fee.feeRate.toFixed(2)}% &lt; 실제{' '}
                  {fee.feeRateActual.toFixed(2)}%
                </div>
              ))}
              계약상 있을 수 있어 저장을 막지는 않습니다. 의도한 값인지 확인해 주세요.
            </>
          }
        />
      )}
      <SettingChangesStyle>
        {changes.map((change, index) => (
          <div className="change-row" key={`${change.label}-${index}`}>
            <div className="change-label">{change.label}</div>
            <div className="change-values">
              <div>
                <small>변경 전</small>
                {change.before}
              </div>
              <span className="change-arrow">→</span>
              <div>
                <small>변경 후</small>
                {change.after}
              </div>
            </div>
          </div>
        ))}
      </SettingChangesStyle>
      <Input.TextArea
        aria-label="변경 사유"
        placeholder="예) 나이스페이 카드 수수료 계약 갱신 (2026-10-01 적용)"
        rows={3}
        maxLength={REASON_MAX_LENGTH}
        showCount
        value={reason}
        disabled={isSaving}
        onChange={(event) => onReasonChange(event.target.value)}
      />
      <p>
        변경 사유는 이력에 함께 남습니다. {REASON_MIN_LENGTH}자 이상 입력해야 저장할 수 있습니다 — 요율이 왜 바뀌었는지는
        나중에 숫자만으로는 알 수 없습니다.
      </p>
    </SettingsModal>
  );
};
